import React, { useCallback, useEffect, useState } from "react";
import { Lifebuoy, CircleNotch, Plus } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { GroupHeading } from "@/components/customer/caravan/buildFields";
import { TicketDialog } from "@/components/customer/tickets/TicketDialog";

const STATUS_CLS = {
  in_progress: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  completed: "bg-[#73bf44]/15 text-[#73bf44] border-[#73bf44]/30",
};
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : "—");

export function useSupportTickets(chassis) {
  const [state, setState] = useState({ loading: true, error: false, tickets: [] });
  const load = useCallback(() => {
    if (!chassis) return;
    api.get("/support-tickets/status", { params: { chassis } })
      .then(({ data }) => setState({ loading: false, error: false, tickets: data.tickets || [] }))
      .catch(() => setState({ loading: false, error: true, tickets: [] }));
  }, [chassis]);
  useEffect(() => { load(); }, [load]);
  return { ...state, reload: load };
}

function TicketRow({ t }) {
  return (
    <div data-testid="support-ticket-item" className="bg-[#141414] border border-neutral-800 rounded-xl px-4 py-3.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-mono text-sm font-semibold text-white" data-testid="support-ticket-ref">{t.reference}</span>
        <span data-testid="support-ticket-status" className={`text-xs font-semibold px-3 py-1 rounded-full border ${STATUS_CLS[t.status] || STATUS_CLS.in_progress}`}>{t.status_label}</span>
      </div>
      <p className="text-sm text-neutral-400 mt-2 leading-relaxed line-clamp-3">{t.summary}</p>
      <div className="text-xs text-neutral-600 mt-2">
        Lodged {fmt(t.created_at)}{t.completed_at ? ` · Completed ${fmt(t.completed_at)}` : ""}
      </div>
    </div>
  );
}

export function SupportTickets({ state }) {
  const [open, setOpen] = useState(false);
  const newBtn = (
    <button onClick={() => setOpen(true)} data-testid="support-tickets-new" className="inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-[#73bf44] transition-colors">
      <Plus size={12} weight="bold" /> New ticket
    </button>
  );
  return (
    <section data-testid="support-tickets" className="bg-[#0F0F0F] border border-neutral-800 rounded-2xl p-5 sm:p-6 max-w-3xl">
      <GroupHeading icon={Lifebuoy} title="Technical Support Tickets" aside={newBtn} />
      {state.loading ? (
        <div className="flex items-center gap-2 text-sm text-neutral-500 py-2"><CircleNotch size={16} className="animate-spin" /> Loading your tickets…</div>
      ) : state.error ? (
        <p className="text-sm text-neutral-500 py-2" data-testid="support-tickets-error">We couldn't load your tickets right now. Please try again shortly.</p>
      ) : state.tickets.length === 0 ? (
        <div className="py-2" data-testid="support-tickets-empty">
          <p className="text-sm text-neutral-400">No support tickets for this caravan. If Nick can't sort out a technical issue, you can lodge one for the OTM tech team.</p>
          <button onClick={() => setOpen(true)} data-testid="support-tickets-lodge" className="mt-3 inline-flex items-center gap-2 text-sm text-[#73bf44] hover:text-[#5fa338] transition-colors font-semibold">
            <Lifebuoy size={16} weight="fill" /> Lodge a support ticket
          </button>
        </div>
      ) : (
        <div className="space-y-2.5" data-testid="support-tickets-list">{state.tickets.map((t) => <TicketRow key={t.reference} t={t} />)}</div>
      )}
      <TicketDialog open={open} onOpenChange={setOpen} onLodged={state.reload} />
    </section>
  );
}
